import { Injectable, Inject, LOCALE_ID, PLATFORM_ID } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import {environment} from '@src/environments/environment';
import {UserProfile} from '@app/interfaces/entities/user-profile';
import { isPlatformBrowser } from '@angular/common';
import { LocaleEnum } from '@app/enums/LocaleEnum';

@Injectable({
  providedIn: 'root'
})
export class LocaleRedirectService {
  constructor(
    private router: Router,
    @Inject(LOCALE_ID) public locale: string,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  /** Every locale is deployed under its own "/{locale}/" prefix - including the source locale (sl). */
  private static readonly BUILD_LOCALES: string[] = Object.values(LocaleEnum);

  /**
   * Compares the user's locale with the current site's locale and redirects if necessary.
   * @param user - User profile containing the locale.
   */
  public compareUserLocaleVsSiteLocaleAndRedirect(user: UserProfile): void {
    if (isPlatformBrowser(this.platformId)) {
      this.redirectToUserLocale(user);
    }
  }

  /**
   * Redirects the user to their locale-specific URL if it differs from the current locale.
   * @param user - User profile containing the locale.
   */
  private redirectToUserLocale(user: UserProfile) {
    if (!isPlatformBrowser(this.platformId) || !user.locale || !environment.production) {
      return;
    }

    const userLocale = user.locale.split('-')[0];

    //  A locale without its own build (e.g. "at") has no URL to redirect to - the request would
    //  fall through to the 404 handler, which serves a different locale's bundle and puts us
    //  straight back here.
    if (userLocale === this.locale || !LocaleRedirectService.BUILD_LOCALES.includes(userLocale)) {
      return;
    }

    const href = this.getCurrentHref();
    const target = this.buildUrlForLocale(href, userLocale);

    //  Assigning window.location.href the URL we are already on is a full page reload into the
    //  exact same state - i.e. an endless refresh loop. Leaving the user on the wrong locale is
    //  the lesser evil.
    if (target !== href) {
      this.navigateTo(target);
    }
  }

  /** Thin wrappers around window.location so tests can spy on them - the native property isn't configurable. */
  protected getCurrentHref(): string {
    return window.location.href;
  }

  protected navigateTo(href: string): void {
    window.location.href = href;
  }

  /**
   * Swaps the "/{locale}/" prefix of the current path for the target locale's, adding it when the
   * path has none (an unprefixed URL is served by the 404 handler, not by the source-locale build).
   */
  private buildUrlForLocale(href: string, targetLocale: string): string {
    const url = new URL(href);
    const segments = url.pathname.split('/').filter(segment => segment.length > 0);

    if (LocaleRedirectService.BUILD_LOCALES.includes(segments[0])) {
      segments.shift();
    }

    url.pathname = `/${targetLocale}/${segments.join('/')}`;

    return url.toString();
  }
}
